import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const IMAGES = [
  "https://images.unsplash.com/photo-1581094488379-6a10d04c0f04?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1538474705339-e87de81450e8?auto=format&fit=crop&w=1600&q=80",
];

const OfficeCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  const timer = useRef(null);
  const { t, lang } = useLanguage();
  const slides = t("carousel.slides");

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  const startAutoplay = useCallback(() => {
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 5500);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", () => setSelected(emblaApi.selectedScrollSnap()));
    startAutoplay();
    return () => clearInterval(timer.current);
  }, [emblaApi, startAutoplay]);

  return (
    <section id="kontor" className="scroll-mt-20 border-t border-slate-200 bg-slate-50 py-20 sm:py-28" data-testid="office-carousel-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-sky-700" data-testid="carousel-badge">
              {t("carousel.badge")}
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" data-testid="carousel-title">
              {t("carousel.title")}
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
              {t("carousel.intro")}
            </p>
          </Reveal>
          <div className="flex gap-3">
            <button
              onClick={scrollPrev}
              data-testid="carousel-prev-btn"
              aria-label="Forrige bilde"
              className="rounded-md border border-slate-300 bg-white p-3 text-slate-600 transition-colors duration-200 hover:border-sky-600 hover:text-sky-700"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={scrollNext}
              data-testid="carousel-next-btn"
              aria-label="Neste bilde"
              className="rounded-md border border-slate-300 bg-white p-3 text-slate-600 transition-colors duration-200 hover:border-sky-600 hover:text-sky-700"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          className="embla corner-frame mt-10 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
          ref={emblaRef}
          onMouseEnter={() => clearInterval(timer.current)}
          onMouseLeave={startAutoplay}
          data-testid="office-carousel"
        >
          <div className="embla__container">
            {slides.map((s, i) => (
              <div className="embla__slide" key={`${lang}-${i}`} data-testid={`carousel-slide-${i}`}>
                <div className="grid lg:grid-cols-5">
                  <div className="relative lg:col-span-3">
                    <img src={IMAGES[i]} alt={s.title} className="h-72 w-full object-cover sm:h-[440px]" />
                    <span className="absolute left-5 top-5 rounded-full border border-white/40 bg-white/90 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-sky-800 backdrop-blur-sm">
                      {s.category}
                    </span>
                  </div>
                  <div className="flex flex-col justify-center p-8 sm:p-12 lg:col-span-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                      {String(i + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
                    </span>
                    <h3 className="mt-4 font-display text-xl font-bold text-slate-900 sm:text-2xl">{s.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">{s.description}</p>
                    <p className="mt-6 border-l-2 border-sky-600 pl-4 font-mono text-xs uppercase tracking-widest text-sky-700">
                      {s.badge}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-center gap-2.5" data-testid="carousel-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              data-testid={`carousel-dot-${i}`}
              aria-label={`Gå til bilde ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                selected === i ? "w-10 bg-sky-600" : "w-4 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OfficeCarousel;
